import React from 'react';
import styled, { css } from 'styled-components';
import { spacing } from '../../style';
import Text, { Label } from '../text';
import Icon from '../icon';
import { variants } from '../title/style';
import { variants as iconVariants } from '../icon/style';
import { Weights } from '../title';
import { TextProps, LabelProps } from '../text/text';
import { IconProps } from '../icon/icon';
import { sizes } from './style';

export interface IconLabelProps {
  icon?: IconProps['name'];
  label?: React.ReactNode;
  variant?: keyof typeof iconVariants & keyof typeof variants;
  size?: keyof typeof sizes;
  className?: string;
  id?: string;
  outlined?: boolean;
  weight?: Weights;
  iconVariant?: keyof typeof iconVariants;
  'data-name'?: string;
  htmlFor?: LabelProps['htmlFor'];
  children?: any;
  inverse?: boolean;
  display?: 'flex' | 'inline-flex';
  'data-testid'?: string;
}

const labelClassName = 'label-icon-text';
const iconClassName = 'label-icon-icon';
const IconLabelComponent = ({
  icon,
  'data-testid': dataTestId,
  label,
  variant,
  size,
  className,
  outlined,
  weight = 400,
  iconVariant,
  'data-name': dataName,
  htmlFor,
  children,
  id,
}: IconLabelProps) => {
  const TextComponent = htmlFor ? Label : Text;
  return (
    <div data-testid={dataTestId} className={className} data-name={dataName}>
      {icon && (
        <Icon className={iconClassName} variant={iconVariant || variant} name={icon} size={size} outlined={outlined} />
      )}
      {label && (
        <TextComponent
          weight={weight}
          className={labelClassName}
          variant={variant}
          size={size}
          htmlFor={htmlFor}
          id={id}
        >
          {label}
        </TextComponent>
      )}

      {children}
    </div>
  );
};

const StyledIconLabel = styled(IconLabelComponent)<IconLabelProps>`
  display: ${props => props.display || 'flex'};

  .label-icon-text {
    ${props =>
      props.inverse
        ? css`
            margin-right: ${spacing.xs};
          `
        : css`
            margin-left: ${spacing.xs};
          `};

    order: ${props => (props.inverse ? 0 : 1)};
  }

  .label-icon-icon {
    order: ${props => (props.inverse ? 1 : 0)};
  }

  * {
    line-height: ${props => sizes[props.size || 'xl']?.lineHeight};
  }
`;

const IconLabel = (props: IconLabelProps) => <StyledIconLabel {...props} />;

IconLabel.Label = (props: LabelProps) => <Label {...props} className={labelClassName} />;
IconLabel.Text = (props: TextProps) => <Text {...props} className={labelClassName} />;
IconLabel.Icon = (props: IconProps) => <Icon {...props} className={iconClassName} />;

export default IconLabel;
